import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth-store';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Permission } from '../../hooks/usePermission';

interface AdminRouteProps {
  children: React.ReactNode;
  /** Single permission required to access this route */
  permission?: Permission;
  /** Multiple permissions - user must have at least one (unless requireAll is true) */
  permissions?: Permission[];
  /** If true, user must have ALL permissions instead of ANY */
  requireAll?: boolean;
}

export function AdminRoute({
  children,
  permission,
  permissions,
  requireAll = false,
}: AdminRouteProps) {
  const { t } = useLanguage();
  const {
    isAuthenticated,
    isAdmin,
    isLoading,
    permissionsLoaded,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  } = useAuthStore();

  // Show loading while auth or permissions are loading
  if (isLoading || (isAdmin && !permissionsLoaded)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/account" replace />;
  }

  // Check specific permission if provided
  if (permission && !hasPermission(permission)) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <div className="text-red-500 text-xl">{t('adminRoute.accessDenied', 'Access Denied')}</div>
        <p className="text-gray-600">
          {t('adminRoute.noPermission', "You don't have permission to access this page.")}
        </p>
        <p className="text-sm text-gray-400">
          {t('adminRoute.required', 'Required')}: <code className="bg-gray-100 px-2 py-1 rounded">{permission}</code>
        </p>
      </div>
    );
  }

  // Check multiple permissions if provided
  if (permissions && permissions.length > 0) {
    const hasAccess = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);

    if (!hasAccess) {
      return (
        <div className="flex flex-col items-center justify-center h-screen gap-4">
          <div className="text-red-500 text-xl">{t('adminRoute.accessDenied', 'Access Denied')}</div>
          <p className="text-gray-600">
            {t('adminRoute.noPermission', "You don't have permission to access this page.")}
          </p>
          <p className="text-sm text-gray-400">
            {t('adminRoute.requiredPermissions', 'Required {type} of', { type: requireAll ? t('adminRoute.all', 'all') : t('adminRoute.any', 'any') })}:{' '}
            {permissions.map((p, i) => (
              <span key={p}>
                <code className="bg-gray-100 px-2 py-1 rounded">{p}</code>
                {i < permissions.length - 1 && ', '}
              </span>
            ))}
          </p>
        </div>
      );
    }
  }

  return <>{children}</>;
}
